<template>
  <v-card
    v-if="showCard"
    id="import-modal-processed-card"
    class="pb-1"
    :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
  >
    <v-card-title>
      {{ $t(`views.imports.importModal.importModalProcessedCard.title`) }}
    </v-card-title>

    <v-card-text class="py-0 mb-0 mt-2">
      <div class="text-center mt-7 mb-4">
        <v-icon color="primary" size="50">mdi-text-box-check</v-icon>
      </div>

      <div class="mt-2 mb-6">
        <p
          v-if="
            myImport.result && myImport.result.summary && myImport.result.summary.successElements
          "
          class="pb-2 ma-0"
        >
          <v-icon color="primary">mdi-check</v-icon>
          {{ $t(`views.imports.importModal.importModalProcessedCard.descriptionCheck1`) }}
        </p>
        <p
          v-if="myImport.result && myImport.result.summary && myImport.result.summary.errorElements"
          class="pb-2 ma-0"
        >
          <v-icon color="primary">mdi-check</v-icon>
          {{
            $t(
              `views.imports.importModal.importModalProcessedCard.descriptionCheckSomeNotProcessed`
            )
          }}
        </p>
        <p
          v-if="myImport.result && myImport.result.summary && myImport.result.summary.errorElements"
          class="pb-2 ma-0"
        >
          <v-icon color="primary">mdi-check</v-icon>
          {{
            $t(
              `views.imports.importModal.importModalProcessedCard.descriptionCheckSomeNotProcessed2`
            )
          }}
        </p>

        <p class="pb-2 ma-0">
          <v-icon color="primary">mdi-check</v-icon>
          {{ $t(`views.imports.importModal.importModalProcessedCard.descriptionCheckUndo1`) }}
          <a @click="$emit('goListMode')">
            {{ $t(`views.imports.importModal.importModalProcessedCard.descriptionCheckUndoLink`) }}
          </a>
          {{ $t(`views.imports.importModal.importModalProcessedCard.descriptionCheckUndo2`) }}
        </p>
      </div>

      <div class="mb-4 pb-2">
        <div class="items-flex">
          <TableBroker :broker="myImport.broker" />
          <div class="mx-2">
            |
          </div>
          <div class="mr-2">
            {{ getDateUserFullFormat(myImport.createdAt) }}
          </div>
        </div>
      </div>

      <v-expansion-panels v-if="myImport.result">
        <v-expansion-panel>
          <v-expansion-panel-header
            :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : undefined"
          >
            <div class="d-flex flex-column">
              <span class="softGreen--text">
                {{ myImport.result.summary.successElements }}
                {{
                  $t(
                    `views.imports.importModal.importModalProcessedCard.result.summary.successElements`
                  )
                }}
              </span>
              <span
                v-if="myImport.result.summary.successElementsWithWarning"
                class="my-3 secondary--text"
              >
                <v-icon color="accentYellow">mdi-alert</v-icon>
                {{ myImport.result.summary.successElementsWithWarning }}
                {{
                  $t(
                    `views.imports.importModal.importModalProcessedCard.result.summary.warningElements`
                  )
                }}
              </span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content
            :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : undefined"
          >
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr v-for="(elem, idx) in myImport.result.successElements" :key="idx">
                    <td>
                      <v-icon v-if="elem.warning" color="accentYellow">mdi-alert</v-icon>
                      <v-icon v-else color="softGreen">mdi-check-circle</v-icon>
                    </td>
                    <td class="text-left">
                      <span v-if="myImport.type === 'order' || myImport.type === 'orderGeneric'">
                        {{ $t(`common.orderTypes.${elem.type}`) }}
                      </span>
                      <span
                        v-else-if="
                          myImport.type === 'dividend' || myImport.type === 'dividendGeneric'
                        "
                      >
                        {{ $t(`common.dividend`) }}
                      </span>
                      <span v-else></span>
                    </td>
                    <td>
                      {{ getDateUserFormat(elem.date) }}
                    </td>
                    <td class="text-left">
                      {{ elem.position.ticker }}
                    </td>
                    <td v-if="myImport.result.summary.successElementsWithWarning">
                      {{ elem.warning ? elem.warning : '' }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header
            :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : undefined"
          >
            <span
              :class="{
                'dark_Textgrey--text': $vuetify.theme.dark,
                'darkTextGrey--text': !$vuetify.theme.dark,
              }"
            >
              {{ myImport.result.summary.errorElements }}
              {{
                $t(
                  `views.imports.importModal.importModalProcessedCard.result.summary.errorElements`
                )
              }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content
            :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : undefined"
          >
            <div v-if="myImport.result.errorElements.length" class="mt-0 mb-2 text-right">
              <ExportButton
                :click="downloadErrorElementsCsv"
                :loading="exportLoading"
                showTextExportCsv
              />
            </div>

            <div v-for="(elem, idx) in myImport.result.errorElements" :key="idx" class="mb-5">
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td class="text-left error-element-row">
                        {{ getRowString(elem.row) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left error-element-error">
                        {{ elem.error }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
    <v-card-actions class="mt-3">
      <a class="list-mode-link ml-5" @click="$emit('goListMode')">
        {{ $t('views.imports.importModal.listMode') }}
      </a>
      <v-spacer />
      <v-btn class="mr-1" text :small="$vuetify.breakpoint.smAndDown" @click="$emit('close')">
        {{ $t('common.close') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { getDateUserFormat, getDateUserFullFormat } from '@/lib/utils/date.js'
import gtag from '@/lib/gtag'
import TableBroker from '@/views/components/common/TableBroker'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { TableBroker, ExportButton },
  props: {
    myImport: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      exportLoading: false,
      //   myImport: {
      //     result: {
      //       summary: {
      //         successElements: 2,
      //         errorElements: 1,
      //       },
      //       successElements: [
      //         {
      //           _id: '62337f0e15e050db6a813ceb',
      //           type: 'buy',
      //           date: '2021-08-06T07:30:01.000Z',
      //           position: {
      //             _id: '6233761e2b6aabc890d9f757',
      //             ticker: 'CLX',
      //           },
      //         },
      //         {
      //           _id: '62337f0e15e050db6a813cf4',
      //           type: 'buy',
      //           date: '2021-08-16T07:30:02.000Z',
      //           position: {
      //             _id: '6233761e2b6aabc890d9f768',
      //             ticker: 'KHC',
      //           },
      //         },
      //       ],
      //       errorElements: [
      //         {
      //           row: {
      //             ClientAccountID: 'U2727023',
      //             AccountAlias: '',
      //             Model: '',
      //             CurrencyPrimary: 'USD',
      //             FXRateToBase: '0.90267',
      //             AssetClass: 'STK',
      //             Symbol: 'RDSB',
      //             Description: 'ROYAL DUTCH SHELL PLC-B SHS',
      //             Conid: '35154827',
      //             SecurityID: 'GB00B03MM408',
      //             SecurityIDType: 'ISIN',
      //             CUSIP: '',
      //             ISIN: 'GB00B03MM408',
      //             ListingExchange: 'LSE',
      //             UnderlyingConid: '',
      //             UnderlyingSymbol: '',
      //             UnderlyingSecurityID: '',
      //             UnderlyingListingExchange: '',
      //             Issuer: '',
      //             Multiplier: '1',
      //             Strike: '',
      //             Expiry: '',
      //             'Put/Call': '',
      //             PrincipalAdjustFactor: '',
      //             ExDate: '20191114',
      //             PayDate: '20191218',
      //             Quantity: '23',
      //             Tax: '0',
      //             Fee: '0',
      //             GrossRate: '0.47',
      //             GrossAmount: '10.81',
      //             NetAmount: '10.81',
      //             Code: '',
      //             FromAcct: '',
      //             ToAcct: '',
      //             SerialNumber: '',
      //             DeliveryType: '',
      //             CommodityType: '',
      //             Fineness: '0.0',
      //             Weight: '0.0 ()',
      //           },
      //           error: 'Error: position not found for dividend',
      //         },
      //         {
      //           row: {
      //             ClientAccountID: 'U2727023',
      //             AccountAlias: '',
      //             Model: '',
      //             CurrencyPrimary: 'USD',
      //             FXRateToBase: '0.90267',
      //             AssetClass: 'STK',
      //             Symbol: 'RDSB',
      //             Description: 'ROYAL DUTCH SHELL PLC-B SHS',
      //             Conid: '35154827',
      //             SecurityID: 'GB00B03MM408',
      //             SecurityIDType: 'ISIN',
      //             CUSIP: '',
      //             ISIN: 'GB00B03MM408',
      //             ListingExchange: 'LSE',
      //             UnderlyingConid: '',
      //             UnderlyingSymbol: '',
      //             UnderlyingSecurityID: '',
      //             UnderlyingListingExchange: '',
      //             Issuer: '',
      //             Multiplier: '1',
      //             Strike: '',
      //             Expiry: '',
      //             'Put/Call': '',
      //             PrincipalAdjustFactor: '',
      //             ExDate: '20191114',
      //             PayDate: '20191218',
      //             Quantity: '23',
      //             Tax: '0',
      //             Fee: '0',
      //             GrossRate: '0.47',
      //             GrossAmount: '10.81',
      //             NetAmount: '10.81',
      //             Code: '',
      //             FromAcct: '',
      //             ToAcct: '',
      //             SerialNumber: '',
      //             DeliveryType: '',
      //             CommodityType: '',
      //             Fineness: '0.0',
      //             Weight: '0.0 ()',
      //           },
      //           error: 'Error: position not found for dividend',
      //         },
      //       ],
      //     },
      //   },
    }
  },
  computed: {
    showCard() {
      const showCard = this.myImport.status === 'processed'

      if (showCard)
        gtag.pageview({
          pagePath: `/input/${this.type === 'order' ? 'orders' : 'dividends'}/import/processed`,
          pageTitle: `input|${this.type === 'order' ? 'orders' : 'dividends'}|import|processed`,
          pageContentGroup: 'modal',
        })

      return showCard
    },
  },
  methods: {
    getDateUserFormat,
    getDateUserFullFormat,
    getRowString(row) {
      const rowString = Object.values(row).toString()
      return rowString
    },
    async downloadErrorElementsCsv() {
      try {
        this.exportLoading = true
        const errorElements = this.myImport.result.errorElements
        if (!errorElements || !errorElements.length) throw new Error('not error elements')

        let csv = Object.keys(errorElements[0].row).toString() + ',ERROR\n'
        for (const elem of errorElements) {
          csv += this.getRowString(elem.row) + `,${elem.error}\n`
        }
        const nowFormatted = DateTime.fromISO(this.myImport.createdAt).toFormat(
          'yyyy_MM_dd HH_mm_ss'
        )

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(blob, `Filios - Import errors (${nowFormatted}).csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#import-modal-processed-card {
  .items-flex {
    display: flex;
    align-content: center;
  }
  p {
    font-size: 15px;
  }

  .list-mode-link {
    font-size: 15px;
    font-weight: 500;
  }

  .v-expansion-panel-header {
    text-align: center;
    font-size: 14px;
    font-weight: 400;
  }
  .v-expansion-panel-content__wrap {
    padding: 0 10px 10px 10px;
  }

  .v-data-table.theme--dark {
    background-color: #3b444c;
  }

  .error-element-row {
    font-style: italic;
  }
  .error-element-error {
    font-weight: 400;
  }
}
</style>
